/**
 * Чертёж к геометрической задаче: SVG из готовой спецификации.
 *
 * **Почему рисуем кодом, а не берём картинку.** Довод тот же, что у
 * `NumberLine`, и в геометрии он сильнее: картинка про вписанную окружность
 * обязана быть верной до касания, а порождённая изображением верна на глаз —
 * окружность проходит рядом со стороной, и ученик принимает это за правило.
 *
 * **Почему здесь нет никакой геометрии.** Все координаты посчитаны в
 * `tools/templates/_figure.py` через SymPy и приехали готовыми. Компонент
 * переносит их на экран и больше ничего не знает: ни про инцентр, ни про
 * касание. Это то же разделение, что «рантайм не разбирает многочлены» в
 * docs/08-answers.md.
 *
 * **Почему подпись «не в масштабе» рисует компонент, а не текст условия.**
 * Чертёж строится по сдвинутым параметрам, чтобы искомое нельзя было снять с
 * экрана транспортиром. Если бы оговорку писал автор шаблона в тексте задачи,
 * её однажды забыли бы — и ученик считал бы измерение законным приёмом. Здесь
 * она следует из того же поля, что и сам сдвиг.
 *
 * **Система координат спецификации математическая**, y вверх; экранная —
 * вниз. Переворот здесь, в одном месте: `sy()`.
 */

type Pt = [number, number];

type Item =
  | { k: "seg"; a: Pt; b: Pt; aux?: boolean }
  | { k: "poly"; pts: Pt[]; close?: boolean }
  | { k: "circle"; c: Pt; r: number; accent?: boolean }
  | { k: "arc"; c: Pt; r: number; from: number; to: number; accent?: boolean }
  | { k: "dot"; at: Pt; accent?: boolean }
  | { k: "dash"; a: Pt; b: Pt }
  | { k: "angle"; at: Pt; from: number; to: number; r?: number; right?: boolean; n?: number }
  | { k: "ticks"; a: Pt; b: Pt; n: number }
  | { k: "label"; at: Pt; text: string };

export type FigureSpec = {
  w: number;
  h: number;
  ox: number;
  oy: number;
  scaled: boolean;
  items: Item[];
};

const isPt = (v: unknown): v is Pt =>
  Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === "number" && Number.isFinite(n));

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/**
 * Обязательные поля каждого примитива.
 *
 * Проверяется не только вид, но и поля: чертёж с координатой-строкой
 * нарисовался бы в NaN, то есть исчез бы молча, а молчащий отказ на странице
 * неотличим от «так и задумано».
 */
const SHAPE: Record<string, (o: Record<string, unknown>) => boolean> = {
  seg: (o) => isPt(o.a) && isPt(o.b),
  dash: (o) => isPt(o.a) && isPt(o.b),
  ticks: (o) => isPt(o.a) && isPt(o.b) && isNum(o.n) && o.n >= 1,
  poly: (o) => Array.isArray(o.pts) && o.pts.length >= 2 && o.pts.every(isPt),
  circle: (o) => isPt(o.c) && isNum(o.r) && o.r > 0,
  arc: (o) => isPt(o.c) && isNum(o.r) && o.r > 0 && isNum(o.from) && isNum(o.to),
  dot: (o) => isPt(o.at),
  angle: (o) =>
    isPt(o.at) &&
    isNum(o.from) &&
    isNum(o.to) &&
    (o.r === undefined || isNum(o.r)) &&
    (o.n === undefined || (isNum(o.n) && o.n >= 1)),
  label: (o) => isPt(o.at) && typeof o.text === "string" && o.text.length > 0,
};

/**
 * `null` — спецификация не разобрана. Экран тогда скажет это словами.
 *
 * Разбор строгий: неизвестный вид примитива роняет весь чертёж, а не
 * пропускается молча. Пропуск дал бы фигуру без одной стороны — картинку,
 * которая выглядит законченной и врёт. Это тот же выбор, что у `VKind` в
 * convex/engine/answer.ts, где неизвестный вид бросает исключение.
 */
export function parseFigure(source: string): FigureSpec | null {
  let raw: unknown;
  try {
    raw = JSON.parse(source);
  } catch {
    return null;
  }
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const nums = ["w", "h", "ox", "oy"] as const;
  if (!nums.every((k) => typeof o[k] === "number" && Number.isFinite(o[k]))) return null;
  if ((o.w as number) <= 0 || (o.h as number) <= 0) return null;
  if (!Array.isArray(o.items) || o.items.length === 0) return null;
  for (const it of o.items) {
    if (typeof it !== "object" || it === null) return null;
    const rec = it as Record<string, unknown>;
    const k = rec.k;
    if (typeof k !== "string") return null;
    const shape = SHAPE[k];
    if (shape === undefined || !shape(rec)) return null;
  }
  return {
    w: o.w as number,
    h: o.h as number,
    ox: o.ox as number,
    oy: o.oy as number,
    scaled: o.scaled === true,
    items: o.items as Item[],
  };
}

/**
 * Перенос спецификации на экран. Ось y переворачивается ЗДЕСЬ и только здесь:
 * в спецификации она направлена вверх, как на бумаге, в SVG — вниз.
 * Отдельной функцией, чтобы переворот можно было проверить тестом.
 */
export function place(spec: FigureSpec): { x: (v: number) => number; y: (v: number) => number } {
  return {
    x: (v: number) => v - spec.ox,
    y: (v: number) => spec.oy + spec.h - v,
  };
}

export function Figure({ source }: { source: string }) {
  const spec = parseFigure(source);
  if (spec === null) {
    return <div className="faint">не удалось нарисовать чертёж</div>;
  }
  const { x: sx, y: sy } = place(spec);
  const P = (p: Pt): string => `${round(sx(p[0]))},${round(sy(p[1]))}`;

  return (
    <figure className="fig">
      {/* width/height в единицах спецификации, а не 100%: единица обязана быть
          пикселем, иначе вытянутая фигура растягивается по ширине и кегль
          подписи раздувается. Треугольник с катетами 9 и 40 приезжал с буквами
          впятеро крупнее, чем у соседней задачи. На узком экране всё
          уменьшается пропорционально — max-width в стилях. */}
      <svg
        className="fig__svg"
        viewBox={`0 0 ${spec.w} ${spec.h}`}
        width={spec.w}
        height={spec.h}
        role="img"
        aria-label="чертёж к задаче"
      >
        {spec.items.map((it, i) => draw(it, i, sx, sy, P))}
      </svg>
      {spec.scaled ? null : <figcaption className="fig__note">рисунок не в масштабе</figcaption>}
    </figure>
  );
}

function draw(
  it: Item,
  key: number,
  sx: (x: number) => number,
  sy: (y: number) => number,
  P: (p: Pt) => string,
) {
  switch (it.k) {
    case "seg":
      return (
        <line
          key={key}
          className={it.aux === true ? "fig__aux" : "fig__line"}
          x1={sx(it.a[0])}
          y1={sy(it.a[1])}
          x2={sx(it.b[0])}
          y2={sy(it.b[1])}
        />
      );

    case "poly":
      return it.close === false ? (
        <polyline key={key} className="fig__line" fill="none" points={it.pts.map(P).join(" ")} />
      ) : (
        <polygon key={key} className="fig__line" fill="none" points={it.pts.map(P).join(" ")} />
      );

    case "circle":
      return (
        <circle
          key={key}
          className={it.accent === true ? "fig__accent" : "fig__line"}
          fill="none"
          cx={sx(it.c[0])}
          cy={sy(it.c[1])}
          r={it.r}
        />
      );

    case "arc":
      return (
        <path
          key={key}
          className={it.accent === true ? "fig__accent" : "fig__line"}
          fill="none"
          d={arcPath(it.c, it.r, it.from, it.to, sx, sy)}
        />
      );

    case "dot":
      return (
        <circle
          key={key}
          className={it.accent === true ? "fig__dot fig__dot--accent" : "fig__dot"}
          cx={sx(it.at[0])}
          cy={sy(it.at[1])}
          r={3}
        />
      );

    case "dash":
      return (
        <line
          key={key}
          className="fig__dash"
          x1={sx(it.a[0])}
          y1={sy(it.a[1])}
          x2={sx(it.b[0])}
          y2={sy(it.b[1])}
        />
      );

    case "angle":
      return it.right === true ? (
        <polyline key={key} className="fig__mark" fill="none" points={rightMark(it.at, it.from, it.to, sx, sy)} />
      ) : (
        // Несколько дуг — как несколько штрихов у равных отрезков: одинаковое
        // их число значит «эти углы равны». Радиусы расходятся, а не толщина:
        // толстая дуга читается как выделение, а не как счёт.
        <g key={key} className="fig__mark">
          {Array.from({ length: it.n ?? 1 }, (_, j) => (
            <path key={j} fill="none" d={arcPath(it.at, (it.r ?? 18) + j * 5, it.from, it.to, sx, sy)} />
          ))}
        </g>
      );

    case "ticks":
      return (
        <g key={key} className="fig__mark">
          {tickMarks(it.a, it.b, it.n, sx, sy).map((d, j) => (
            <line key={j} x1={d[0]} y1={d[1]} x2={d[2]} y2={d[3]} />
          ))}
        </g>
      );

    case "label":
      return (
        <text
          key={key}
          className="fig__label"
          x={sx(it.at[0])}
          y={sy(it.at[1])}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {it.text}
        </text>
      );
  }
}

/** Точка на окружности по математическому углу, уже в экранных координатах. */
function on(c: Pt, r: number, deg: number, sx: (x: number) => number, sy: (y: number) => number): [number, number] {
  const t = (deg * Math.PI) / 180;
  return [sx(c[0] + r * Math.cos(t)), sy(c[1] + r * Math.sin(t))];
}

function arcPath(
  c: Pt,
  r: number,
  from: number,
  to: number,
  sx: (x: number) => number,
  sy: (y: number) => number,
): string {
  const span = ((to - from) % 360 + 360) % 360;
  const [x1, y1] = on(c, r, from, sx, sy);
  const [x2, y2] = on(c, r, to, sx, sy);
  // Против часовой в математике — по часовой на экране: ось y перевёрнута.
  return `M ${round(x1)} ${round(y1)} A ${r} ${r} 0 ${span > 180 ? 1 : 0} 0 ${round(x2)} ${round(y2)}`;
}

/** Квадратик прямого угла: два отрезка по направлениям сторон. */
function rightMark(
  at: Pt,
  from: number,
  to: number,
  sx: (x: number) => number,
  sy: (y: number) => number,
): string {
  const s = 11;
  const [ax, ay] = on(at, s, from, sx, sy);
  const [bx, by] = on(at, s, to, sx, sy);
  const cx = ax + bx - sx(at[0]);
  const cy = ay + by - sy(at[1]);
  return `${round(ax)},${round(ay)} ${round(cx)},${round(cy)} ${round(bx)},${round(by)}`;
}

/** Штрихи равенства: n чёрточек поперёк середины отрезка. */
function tickMarks(
  a: Pt,
  b: Pt,
  n: number,
  sx: (x: number) => number,
  sy: (y: number) => number,
): [number, number, number, number][] {
  const ax = sx(a[0]);
  const ay = sy(a[1]);
  const bx = sx(b[0]);
  const by = sy(b[1]);
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const out: [number, number, number, number][] = [];
  const gap = 4;
  const half = 5;
  for (let i = 0; i < n; i++) {
    const t = 0.5 * len + (i - (n - 1) / 2) * gap;
    const px = ax + ux * t;
    const py = ay + uy * t;
    out.push([
      round(px - uy * half),
      round(py + ux * half),
      round(px + uy * half),
      round(py - ux * half),
    ]);
  }
  return out;
}

function round(v: number): number {
  return Math.round(v * 100) / 100;
}
